/** Shared data shapes and helpers for the map export legend.
 *
 * The legend is computed once (in ExportModal) and consumed twice: rendered as
 * a live React panel in the modal, and drawn as vector text/shapes into the
 * PDF. Both consumers read the same `ExportLegend` object so they can never
 * disagree about what's on the page.
 */

export interface LegendSwatch {
  label: string;
  color: string;
}

export interface LegendLayer {
  label: string;
  color: string;
  opacity: number;
  /** true → filled square swatch (polygon layers like NSG / FFH areas);
   *  false → line swatch (linear layers like waterways). */
  fill: boolean;
}

export interface LegendSpecies {
  label: string;
  color: string;
  /** Whether this species' colour is actually drawn on the map for the current
   *  frame — in habitat mode when habitat is in-frame and the species layer is
   *  on, and in planning mode while the species-coloured overview tier is
   *  showing with habitat in-frame. When false, the legend shows just the name. */
  showColor: boolean;
}

export interface ExportLegend {
  /** Maintenance-measure swatches present inside the framed extent. Planning
   *  mode only (empty in habitat mode). */
  measures: LegendSwatch[];
  /** Whether any "no work permitted" (red dashed) habitat falls in the frame.
   *  Planning mode only. */
  hasNoWork: boolean;
  /** Every species selected in the sidebar filters, listed once. A colour
   *  swatch is shown only for those whose habitat is in the framed window;
   *  the rest are name-only (selected, but not drawn in colour here). */
  species: LegendSpecies[];
  /** Visible base layers (protected areas, waterways, …). Shown as legend
   *  entries whenever their layer toggle is on, regardless of mode. */
  layers: LegendLayer[];
  /** Human-readable summary of the month filter, e.g. "Apr–Jun, Oct". */
  monthsLabel: string;
  /** Whether to show the time-period block. Planning mode only — the month
   *  filter doesn't scope what's drawn in habitat mode, so it's dropped there. */
  showTimePeriod: boolean;
}

/** Format the selected-months set as a compact, readable string.
 *
 * - all 12 selected → `allLabel` (e.g. "All months" / "ganzjährig")
 * - contiguous runs collapse to ranges ("Apr–Jun")
 * - a two-month run stays comma-joined ("Apr, May") since a range dash for two
 *   adjacent months reads as heavier than it's worth
 * - non-contiguous groups join with commas ("Jan–Mar, Jul, Oct–Dec")
 *
 * `labels` is a 0-indexed array of month names (index 0 = January); month
 * numbers in the set are 1-indexed.
 */
export function formatSelectedMonths(
  months: Set<number>,
  labels: string[],
  allLabel: string,
): string {
  if (months.size === 0) return "—";
  if (months.size >= 12) return allLabel;

  const sorted = [...months].sort((a, b) => a - b);
  const parts: string[] = [];
  let runStart = sorted[0];
  let prev = sorted[0];

  const closeRun = (start: number, end: number) => {
    if (start === end) {
      parts.push(labels[start - 1]);
    } else if (end === start + 1) {
      parts.push(`${labels[start - 1]}, ${labels[end - 1]}`);
    } else {
      parts.push(`${labels[start - 1]}–${labels[end - 1]}`);
    }
  };

  for (let i = 1; i <= sorted.length; i++) {
    const cur: number | undefined = i < sorted.length ? sorted[i] : undefined;
    if (cur !== undefined && cur === prev + 1) {
      prev = cur;
      continue;
    }
    closeRun(runStart, prev);
    if (cur !== undefined) {
      runStart = cur;
      prev = cur;
    }
  }

  return parts.join(", ");
}
